import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, UserRound } from "lucide-react";
import Seo from "@/components/Seo";
import ShortAnswer from "@/components/ShortAnswer";
import { AUTHOR } from "@/data/author";
import { HUB_PATHS, CHARACTERISTICS, CHECKED_ON } from "@/data/equality";
import { Hero, CheckerCard, breadcrumb, linkClass } from "./shared";

// Vanessa's own words, approved by her on 31 October 2026. Do not edit
// without her say-so, and never name the employer.
const WHY = [
  "I was working as in-house counsel when I lost my job because I was pregnant. It was my third child, and I was the sole breadwinner.",
  "That's when I decided I would never work for anyone else again, and I started my own business while I was still pregnant.",
  "I'm a solicitor. I knew the law, and it still happened. This hub exists so that employers know how to get it right, and so that the people who work for them know where they stand.",
];

const DOORS = [
  {
    to: HUB_PATHS.employers,
    icon: Briefcase,
    eyebrow: "For employers",
    title: "I run a business",
    text: "Equality that pays: what it costs to get wrong, what you can claw back, and the fair way to say yes or no.",
    testid: "door-employers",
  },
  {
    to: HUB_PATHS.employees,
    icon: UserRound,
    eyebrow: "For people at work",
    title: "I work for one",
    text: "Your rights in plain English, especially for women at work: pregnancy, health, pay, flexibility and harassment.",
    testid: "door-employees",
  },
];

export default function EqualityHome() {
  const origin = window.location.origin;
  const pageUrl = `${origin}${HUB_PATHS.home}`;
  return (
    <main data-testid="equality-home">
      <Seo
        title="Equality at Work: Guides for Employers and Employees (UK) | Electra HR"
        siteName="Electra HR"
        description="Free UK guides to equality at work, for employers and employees. What the Equality Act 2010 protects, best practice that makes commercial sense, and your rights at work, written by a solicitor."
        image="/og/default.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": pageUrl,
              url: pageUrl,
              name: "Equality at work",
              isPartOf: { "@id": `${origin}/#website` },
              publisher: { "@id": `${origin}/#organization` },
              author: { "@type": "Person", name: AUTHOR.name, jobTitle: "Solicitor" },
              dateModified: CHECKED_ON.iso,
            },
            breadcrumb(origin, [
              { name: "Home", path: "/" },
              { name: "Equality at work", path: HUB_PATHS.home },
            ]),
          ],
        }}
      />

      <Hero
        testid="equality-home-hero"
        eyebrow="Equality at work"
        title="Equality is good business."
        accent="Both sides of the desk."
        intro="Free guides for employers and for the people who work for them. Same law, same facts, and best practice that works for both."
      />

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x max-w-5xl">
          <ShortAnswer path={HUB_PATHS.home} className="mb-12" />

          <div className="grid gap-6 md:grid-cols-2">
            {DOORS.map(({ to, icon: Icon, eyebrow, title, text, testid }) => (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className="group border-2 border-ink bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#F472B6]"
              >
                <Icon size={26} className="text-pink" />
                <p className="mt-5 eyebrow text-ink/50">{eyebrow}</p>
                <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight">{title}</h2>
                <p className="mt-3 text-base leading-relaxed text-ink/70">{text}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-ink/60 group-hover:text-ink">
                  Go to the hub <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-10">
            <CheckerCard to={HUB_PATHS.checker} testid="home-checker-card" />
          </div>

          <section data-testid="why-this-hub-exists" className="mt-16 border-l-4 border-pink bg-white p-7 sm:p-9">
            <p className="eyebrow text-ink/50">Why this hub exists</p>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-ink/80">
              {WHY.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </div>
            <p className="mt-6 font-semibold text-ink">
              <Link to={AUTHOR.path} className={linkClass}>{AUTHOR.name}</Link>, {AUTHOR.credential}
            </p>
          </section>

          <section className="mt-16">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">The nine protected characteristics</h2>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink/70">
              The Equality Act 2010 protects these nine, and only these nine (
              <a href="https://www.legislation.gov.uk/ukpga/2010/15/section/4" target="_blank" rel="noopener noreferrer" className={linkClass}>s.4</a>
              ). Plenty of things people assume are on the list, like mental health or menopause, are only covered through one of them.
            </p>
            <dl className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {CHARACTERISTICS.map((c) => (
                <div key={c.name} className="border-2 border-ink/15 bg-white p-5">
                  <dt className="font-display text-lg font-semibold">{c.name}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-ink/70">{c.plain}</dd>
                  <dd className="mt-3 text-xs">
                    <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-ink/55 underline decoration-pink underline-offset-2 hover:text-ink">
                      Equality Act 2010, {c.section}
                    </a>
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </section>
    </main>
  );
}
